/*
   The slice of an OpenAPI 3 document this page reads, and the helpers that turn it into the
   reference. Everything arrives as `unknown` (the schema is the backend's, not ours) and is
   narrowed here, so the page never trusts a shape it did not check.
*/

export type JsonObject = Record<string, unknown>

export function isObject(value: unknown): value is JsonObject {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}

function text(value: unknown): string {
  return typeof value === "string" ? value : ""
}

export interface Param {
  name: string
  where: string
  required: boolean
  type: string
  description: string
}

export interface Field {
  name: string
  type: string
  required: boolean
  description: string
}

export interface Operation {
  key: string
  method: string
  path: string
  tag: string
  summary: string
  description: string
  params: Param[]
  /** The request body's media type and its fields, one level deep. */
  body: { mediaType: string; schemaName: string; fields: Field[] } | null
  responses: { status: string; description: string; schemaName: string }[]
}

export interface ApiReference {
  title: string
  version: string
  tags: { name: string; description: string; operations: Operation[] }[]
  operationCount: number
}

const METHODS = ["get", "post", "put", "patch", "delete"] as const

/** `#/components/schemas/Foo` → the Foo schema, or the schema itself when it is inline. */
function resolve(schema: unknown, doc: JsonObject): { name: string; schema: JsonObject } | null {
  if (!isObject(schema)) return null
  const ref = text(schema["$ref"])
  if (!ref) return { name: "", schema }
  const name = ref.split("/").pop() ?? ""
  const components = isObject(doc["components"]) ? doc["components"] : {}
  const schemas = isObject(components["schemas"]) ? components["schemas"] : {}
  const target = schemas[name]
  return isObject(target) ? { name, schema: target } : { name, schema: {} }
}

/** A readable type for a schema: `string`, `integer[]`, `EvalMark`, `string | null`. */
export function typeOf(schema: unknown): string {
  if (!isObject(schema)) return "any"
  const ref = text(schema["$ref"])
  if (ref) return ref.split("/").pop() ?? "object"
  const anyOf = schema["anyOf"] ?? schema["oneOf"]
  if (Array.isArray(anyOf)) return anyOf.map(typeOf).join(" | ")
  const type = text(schema["type"])
  if (type === "array") return `${typeOf(schema["items"])}[]`
  if (type) {
    const format = text(schema["format"])
    return format === "binary" ? "file" : type
  }
  return "object"
}

function fieldsOf(schema: JsonObject): Field[] {
  const properties = isObject(schema["properties"]) ? schema["properties"] : {}
  const required = Array.isArray(schema["required"]) ? schema["required"].map(text) : []
  return Object.entries(properties).map(([name, prop]) => ({
    name,
    type: typeOf(prop),
    required: required.includes(name),
    description: isObject(prop) ? text(prop["description"]) : "",
  }))
}

function contentSchema(content: unknown): { mediaType: string; schema: unknown } | null {
  if (!isObject(content)) return null
  const [mediaType, media] = Object.entries(content)[0] ?? []
  if (!mediaType || !isObject(media)) return null
  return { mediaType, schema: media["schema"] }
}

function operationOf(path: string, method: string, op: JsonObject, doc: JsonObject): Operation {
  const tags = Array.isArray(op["tags"]) ? op["tags"].map(text) : []
  const params: Param[] = (Array.isArray(op["parameters"]) ? op["parameters"] : [])
    .filter(isObject)
    .map((p) => ({
      name: text(p["name"]),
      where: text(p["in"]),
      required: p["required"] === true,
      type: typeOf(p["schema"]),
      description: text(p["description"]),
    }))

  let body: Operation["body"] = null
  const requestBody = op["requestBody"]
  if (isObject(requestBody)) {
    const content = contentSchema(requestBody["content"])
    const resolved = content ? resolve(content.schema, doc) : null
    if (content && resolved) {
      body = {
        mediaType: content.mediaType,
        schemaName: resolved.name,
        fields: fieldsOf(resolved.schema),
      }
    }
  }

  const responses = Object.entries(isObject(op["responses"]) ? op["responses"] : {}).map(
    ([status, response]) => {
      const content = isObject(response) ? contentSchema(response["content"]) : null
      return {
        status,
        description: isObject(response) ? text(response["description"]) : "",
        schemaName: content ? typeOf(content.schema) : "",
      }
    },
  )

  return {
    key: `${method} ${path}`,
    method: method.toUpperCase(),
    path,
    tag: tags[0] ?? "other",
    summary: text(op["summary"]),
    description: text(op["description"]),
    params,
    body,
    responses,
  }
}

/** The document as tag groups, in the order the backend declares its tags. */
export function toReference(doc: unknown): ApiReference {
  if (!isObject(doc)) return { title: "", version: "", tags: [], operationCount: 0 }
  const info = isObject(doc["info"]) ? doc["info"] : {}
  const paths = isObject(doc["paths"]) ? doc["paths"] : {}

  const operations: Operation[] = []
  for (const [path, item] of Object.entries(paths)) {
    if (!isObject(item)) continue
    for (const method of METHODS) {
      const op = item[method]
      if (isObject(op)) operations.push(operationOf(path, method, op, doc))
    }
  }

  const declared = (Array.isArray(doc["tags"]) ? doc["tags"] : []).filter(isObject).map((t) => ({
    name: text(t["name"]),
    description: text(t["description"]),
  }))
  const order = new Map(declared.map((t, i) => [t.name, i]))
  const byTag = new Map<string, Operation[]>()
  for (const op of operations) byTag.set(op.tag, [...(byTag.get(op.tag) ?? []), op])

  const tags = [...byTag.entries()]
    .sort(
      ([a], [b]) =>
        (order.get(a) ?? Number.MAX_SAFE_INTEGER) - (order.get(b) ?? Number.MAX_SAFE_INTEGER) ||
        a.localeCompare(b),
    )
    .map(([name, ops]) => ({
      name,
      description: declared.find((t) => t.name === name)?.description ?? "",
      operations: ops,
    }))

  return {
    title: text(info["title"]),
    version: text(info["version"]),
    tags,
    operationCount: operations.length,
  }
}

/** Case-insensitive match on path, method, summary and tag. */
export function filterReference(ref: ApiReference, query: string): ApiReference["tags"] {
  const q = query.trim().toLowerCase()
  if (!q) return ref.tags
  return ref.tags
    .map((t) => ({
      ...t,
      operations: t.operations.filter((op) =>
        [op.path, op.method, op.summary, op.tag].some((s) => s.toLowerCase().includes(q)),
      ),
    }))
    .filter((t) => t.operations.length > 0)
}
